import React, { useMemo } from 'react';
import { Button, Loader, Table, useToasts } from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { History } from 'history';
import { get, isNumber, uniq } from 'lodash';
import moment from 'moment-timezone';
import qs from 'query-string';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import CreatedBy from 'routes/components/CreatedBy';
import { SAVED_TEMPLATES_LIMIT, TEMPLATES_PER_PAGE } from 'routes/constants';
import { useExportSurvey } from 'shared/hooks';
import {
  AccountInfoType,
  ConfirmationType,
  SurveyType,
  SurveysResponseType,
  UserType,
} from 'shared/types';
import { getAccountTimeFormat } from 'shared/utils';
import EmptyState from '../../../../../shared/components/EmptyState';
import Icon from '../../../../../shared/components/Icon';
import {
  Center,
  FormContainer,
  FormContent,
  FormHeader,
} from '../../../../../shared/styles';
import { getText } from '../../../../../shared/text';
import { useGetTenantInfo } from '../../hooks';
import {
  useDeleteTemplate,
  useGetTemplateById,
  useGetTemplates,
  useSaveTemplate,
} from './hooks';
import {
  TemplateDescriptionFormType,
  getSurveyTemplateBasicSchema,
  getTemplateFormValues,
  getTemplatePayload,
} from './utils';
import { DisplayBox, TableDataContainer } from '../../../../styles';
import { SameLineField, SurveyName } from './styles';
import TemplateDescriptionForm from './template-description-form';

const {
  settings: { savedtemplates },
} = getText('EN');

type TemplatesProps = {
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
};

const Templates = ({ setConfirmationData }: TemplatesProps) => {
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  const { push }: History = useHistory();
  const queryClient = useQueryClient();

  const [pageToken, setPageToken] = React.useState<any>({ 0: '' });
  const [pageIndex, setPageIndex] = React.useState<number>(0);
  const [surveys, setSurveys] = React.useState<SurveyType[]>();
  const [count, setCount] = React.useState<number>(0);
  const [templateToExport, setTemplateToExport] = React.useState<string | null>(
    null,
  );
  const [templateIdToFetch, setTemplateIdToFetch] = React.useState<string>();
  const [fetchedTemplate, setFetchedTemplate] = React.useState<SurveyType>({});
  const accountInfo: AccountInfoType =
    queryClient.getQueryData('accountinfo') || {};

  const userInfo: UserType = queryClient.getQueryData('loggedInUser') || {};

  const templateSuccessHandler = (response: SurveyType) => {
    setFetchedTemplate(getTemplateFormValues({ template: response }));
    reset({
      desc: response.desc,
      name: `${t('copy_of', { label: response.name || '' })}`.slice(0, 50),
    });
  };

  const { data: tenantInfo } = useGetTenantInfo();

  const { isLoading: isLoadingTemplate } = useGetTemplateById({
    templateId: templateIdToFetch || '',
    callback: (isSuccess: boolean, response: SurveyType) => {
      if (isSuccess) {
        templateSuccessHandler(response);
      }
    },
  });

  const {
    isFetching: isFetchingTemplates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
    refetch: refetchTemplates,
  } = useGetTemplates({
    pageToken: pageToken[pageIndex] || '',
    callback: async (isSuccess: boolean, response: SurveysResponseType) => {
      if (isSuccess) {
        const responseCount = get(response, ['_links', 'count'], 0);
        setCount(isNumber(Number(responseCount)) ? Number(responseCount) : 0);
        setSurveys(response.surveys);
        const nextURL = get(response, ['_links', 'next', 'href'], '');
        if (nextURL) {
          const parseData: any = qs.parseUrl(nextURL);
          if (parseData.query?.next_token) {
            setPageToken((pgTkn) => ({
              ...pgTkn,
              [pageIndex + 1]: parseData.query.next_token,
            }));
          }
        }
      }
    },
  });
  const { mutate: saveTemplate, isLoading: isSavingTemplate } = useSaveTemplate(
    {
      setToast,
      callback: (isSuccess: boolean, templateSaveResponse: any) => {
        if (isSuccess) {
          setFetchedTemplate({});
          setTemplateIdToFetch('');
          if (templateSaveResponse?.id) {
            push(`/settings/templates/${templateSaveResponse.id}/edit`);
          }
        } else {
          if (
            templateSaveResponse?.response?.data === 'Duplicate Template name'
          ) {
            templateDescriptionForm.setError('name', {
              message: `${t('forms.toast.error.template_name_exists_already')}`,
            });
          } else {
            setToast({
              type: 'error',
              delay: 3000,
              text: `${t('forms.toast.error.template_save_failed')}`,
            });
          }
        }
      },
    },
  );
  const [takenNames, setTakenNames] = React.useState<string[]>([]);
  React.useEffect(() => {
    const surveyNames: string[] = (surveys || [])
      .map((eachSurvey: SurveyType) => eachSurvey.name || '')
      .filter((surveyNames) => surveyNames !== '');
    setTakenNames((old) => uniq([...old, ...surveyNames]));
  }, [surveys]);

  const templateDescriptionForm = useForm<TemplateDescriptionFormType>({
    defaultValues: {
      name: '',
      desc: '',
    },
    mode: 'all',
    resolver: yupResolver(
      getSurveyTemplateBasicSchema({
        takenNames: takenNames || [],
      }),
    ),
  });

  const { handleSubmit, reset } = templateDescriptionForm;

  const { mutate: deleteTemplate, isLoading: isDeletingTemplate } =
    useDeleteTemplate({
      setToast,
      callback: (isSuccess, response, template) => {
        if (isSuccess) {
          setTakenNames((old) =>
            old.filter((eachSurvey: string) => template.name !== eachSurvey),
          );
          setConfirmationData({});
        }
      },
    });

  const { isLoading: isDownloadingFile } = useExportSurvey({
    as: 'template',
    surveyId: templateToExport ?? '',
    enabled: templateToExport !== null,
    callback: (success, errorMsg) => {
      setTemplateToExport(null);

      if (!success) {
        setToast({
          type: 'error',
          delay: 3000,
          text: `${t(errorMsg)}`,
        });
      }
    },
  });

  const isTemplateActionsDisabled = useMemo(
    () =>
      isDeletingTemplate ||
      isSavingTemplate ||
      isLoadingTemplate ||
      count >=
        (Number(accountInfo?.resource_limit_configuration?.templates) ||
          Number(tenantInfo?.limit_config?.no_of_saved_templates) ||
          SAVED_TEMPLATES_LIMIT),
    [
      accountInfo?.resource_limit_configuration?.templates,
      count,
      isDeletingTemplate,
      isLoadingTemplate,
      isSavingTemplate,
      tenantInfo?.limit_config?.no_of_saved_templates,
    ],
  );

  React.useEffect(() => {
    refetchTemplates();
  }, [pageIndex]);

  React.useEffect(() => {
    refetchTemplates();
  }, []);

  if (isLoadingTemplates)
    return (
      <Center minHeight="calc(100vh - 136px)">
        <Loader />
      </Center>
    );

  if (isTemplatesError)
    return (
      <Center minHeight="calc(100vh - 136px)">
        {t('empty_state.message.network_error')}
      </Center>
    );

  const onDuplicateTemplate = (data: TemplateDescriptionFormType) => {
    saveTemplate({
      payload: getTemplatePayload({
        template: {
          ...fetchedTemplate,
          desc: data.desc,
          name: data.name,
          id: undefined,
        },
      }),
      originSurveyId: fetchedTemplate.id,
      id: data.created_at ? data.id : undefined,
    });
  };

  return (
    <>
      {fetchedTemplate?.id && (
        <FormProvider {...templateDescriptionForm}>
          <TemplateDescriptionForm
            isLoading={isSavingTemplate}
            onDismiss={() => {
              setFetchedTemplate({});
              setTemplateIdToFetch('');
              reset({});
            }}
            onSubmit={() =>
              handleSubmit(onDuplicateTemplate, (err) =>
                console.log('ERR', err),
              )()
            }
            title={t('settings.saved_templates.duplicate_template')}
          />
        </FormProvider>
      )}

      <FormContainer
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
        }}
      >
        <FormHeader hideBorder>
          <div className="left-pane">
            <div>
              <div className="heading">{`${t(
                savedtemplates?.layout?.heading,
              )} (${count}/${
                accountInfo.resource_limit_configuration?.templates ||
                tenantInfo?.limit_config?.no_of_saved_templates ||
                SAVED_TEMPLATES_LIMIT
              })`}</div>
              <div className="sub-heading">
                {t(savedtemplates?.layout?.description)}
              </div>
            </div>
          </div>
          <div className="right-pane">
            {/* <Input
            placeholder={savedtemplates?.fields?.search?.placeholder}
            startAdornment={
              <Adornment>
                <Icon width="17" height="17" glyph="search" />
              </Adornment>
            }
            inline
          /> */}
            <Button
              id="import-template-btn"
              type="secondary"
              onClick={() => push('/settings/templates/create?import=true')}
              inline
              disabled={isTemplateActionsDisabled}
            >
              <Icon glyph="upload" width="14" height="14" viewBox="0 0 14 15" />
              {t('import')}
            </Button>
            <Button
              id="new-template-btn"
              onClick={() => push('/settings/templates/create')}
              inline
              disabled={isTemplateActionsDisabled}
            >
              {t(`${savedtemplates?.action?.okButton}`)}
            </Button>
          </div>
        </FormHeader>
        <FormContent>
          <TableDataContainer className="templates-table">
            <Table
              data={surveys || []}
              hideTableOnEmpty
              columns={[
                {
                  id: 'name',
                  Header:
                    t(`${savedtemplates?.fields?.columnSurvey?.name}`) || '',
                  accessor: ({ name, desc, id }) => (
                    <SurveyName>
                      <div className="survey-name">
                        <a
                          onClick={() => push(`/settings/templates/${id}/edit`)}
                          title={name || ''}
                        >
                          {name}
                        </a>
                      </div>
                      <div className="survey-desc">{desc}</div>
                    </SurveyName>
                  ),
                },
                {
                  id: 'theme',
                  Header:
                    t(`${savedtemplates?.fields?.columnTheme?.name}`) || '',
                  accessor: ({ theme_type }) => (
                    <SameLineField>
                      <div
                        style={{
                          fontSize: '12px',
                          display: 'flex',
                          gap: '4px',
                        }}
                      >
                        {theme_type?.length ? (
                          <>
                            {theme_type?.includes('NONE') ? (
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: 'var(--colors-text-placeholder)',
                                }}
                              >
                                -
                              </span>
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                }}
                              >
                                {theme_type?.join(', ')}
                              </div>
                            )}
                          </>
                        ) : (
                          <>-</>
                        )}
                      </div>
                    </SameLineField>
                  ),
                },
                {
                  id: 'created',
                  Header:
                    t(`${savedtemplates?.fields?.columnCrBy?.name}`) || '',
                  accessor: ({ meta, created_at }: SurveyType) => {
                    const { created_by = '' } = meta || {};
                    return (
                      <SameLineField>
                        <CreatedBy
                          userId={created_by || userInfo?.display_id || ''}
                        />
                        <div>
                          {created_at
                            ? moment(new Date(created_at))
                                .tz(accountInfo?.timezone || moment.tz.guess())
                                .format(getAccountTimeFormat(accountInfo))
                            : ''}
                        </div>
                      </SameLineField>
                    );
                  },
                },
                {
                  id: 'updated',
                  Header:
                    t(`${savedtemplates?.fields?.columnCrOn?.name}`) || '',
                  accessor: ({ meta, updated_at }: SurveyType) => {
                    const { updated_by, created_by } = meta || {};
                    return (
                      <SameLineField>
                        <CreatedBy
                          userId={
                            updated_by ||
                            created_by ||
                            userInfo?.display_id ||
                            ''
                          }
                        />
                        <div>
                          {updated_at
                            ? moment(new Date(updated_at))
                                .tz(accountInfo?.timezone || moment.tz.guess())
                                .format(getAccountTimeFormat(accountInfo))
                            : ''}
                        </div>
                      </SameLineField>
                    );
                  },
                },
              ]}
              count={count}
              disableRowSelect
              isLoading={!surveys || isLoadingTemplates}
              loaderStyles={{ minHeight: 'calc(100vh - 192px)' }}
              hideActions
              hideFilters
              pageIndex={pageIndex}
              isFetching={isFetchingTemplates}
              pageSize={TEMPLATES_PER_PAGE}
              hideBottomPagination={count < TEMPLATES_PER_PAGE}
              setPageIndex={(pgNo: number) => setPageIndex(pgNo)}
              onMenuOptionSelect={(option: string, rowData: SurveyType) => {
                let templateList: any = {};
                switch (option) {
                  case 'duplicate':
                    templateList = queryClient.getQueryData([
                      'templates',
                      rowData.id,
                    ]);
                    if (templateList?.id) {
                      templateSuccessHandler(templateList);
                    } else {
                      setTemplateIdToFetch(rowData?.id || '');
                    }

                    break;
                  case 'edit':
                    push(`/settings/templates/${rowData.id}/edit`);
                    break;
                  case 'delete':
                    setConfirmationData({
                      id: new Date().toISOString(),
                      title: `${t('confirm_deletion')}`,
                      className: 'error',
                      confirmButtonText: `${t('delete')}`,
                      onConfirm: () => {
                        setConfirmationData((old) => ({
                          ...old,
                          confirmButtonDisabled: true,
                        }));
                        deleteTemplate(rowData || '');
                      },
                      data: rowData,
                      fill: '#d7461f',
                      infoText: `${t('contacts.menu.delete.infotext')}`,
                      description: `${t(
                        'contacts.menu.delete_selected_contact',
                        { label: rowData.name },
                      )}`,
                      glyph: 'info',
                    });

                    break;
                  case 'export':
                    setTemplateToExport(rowData.id || null);
                    break;
                }
              }}
              rowMenuOptions={() => [
                {
                  id: 'edit',
                  name: t('edit'),
                },
                {
                  id: 'duplicate',
                  name: t('duplicate'),
                  disabled:
                    count >=
                    (Number(
                      accountInfo?.resource_limit_configuration?.templates,
                    ) || SAVED_TEMPLATES_LIMIT),
                },
                {
                  id: 'export',
                  disabled: isDownloadingFile,
                  name: t('export'),
                },
                {
                  id: 'delete',
                  name: t('delete'),
                },
              ]}
              emptyComponent={
                <EmptyState
                  minHeight="calc(100vh - 276px)"
                  title={t(`${savedtemplates?.fields?.empty?.name}`) || ''}
                  message={
                    isTemplatesError
                      ? `${t('empty_state.message.network_error')}`
                      : `${t('empty_state.message.no_templates')}`
                  }
                  imgSrc="empty-state-contacts.svg"
                >
                  <DisplayBox className="info">
                    <div>
                      <Icon width="16" height="16" glyph="info" />
                    </div>
                    <div className="divider" />
                    <div className="text-content">
                      {t(`${savedtemplates?.fields?.empty?.hint}`) || ''}
                    </div>
                  </DisplayBox>
                </EmptyState>
              }
            />
          </TableDataContainer>
        </FormContent>
      </FormContainer>
    </>
  );
};

export default Templates;
